/**
 * Data transformation utilities for OD analysis
 */

/**
 * Filter trips data by day type and time slot
 * @param {object} tripsData - Full trips data object
 * @param {string} dayType - Day type (e.g., "Feriali")
 * @param {string} timeSlot - Time slot (e.g., "08:00 - 09:00")
 * @returns {Array} Array of trip objects for the specified time
 */
export const filterTripsByDateTime = (tripsData, dayType, timeSlot) => {
  if (!tripsData || !dayType || !timeSlot) {
    return [];
  }

  const dayData = tripsData[dayType];
  if (!dayData) {
    return [];
  }

  return dayData[timeSlot] || [];
};

/**
 * Get trips count for a specific origin-destination pair
 * @param {Array} trips - Array of trip objects
 * @param {string} origin - Origin zone name
 * @param {string} destination - Destination zone name
 * @returns {number} Number of trips
 */
export const getTripsForOD = (trips, origin, destination) => {
  if (!trips || !origin || !destination) {
    return 0;
  }

  const trip = trips.find(
    (t) => t.origin === origin && t.destination === destination
  );

  return trip ? trip.trips : 0;
};

/**
 * Get all trips from a specific origin
 * @param {Array} trips - Array of trip objects
 * @param {string} origin - Origin zone name
 * @returns {Array} Array of { destination, trips } objects
 */
export const getTripsFromOrigin = (trips, origin) => {
  if (!trips || !origin) {
    return [];
  }

  return trips
    .filter((t) => t.origin === origin)
    .map((t) => ({ zone: t.destination, trips: t.trips }));
};

/**
 * Get all trips to a specific destination
 * @param {Array} trips - Array of trip objects
 * @param {string} destination - Destination zone name
 * @returns {Array} Array of { origin, trips } objects
 */
export const getTripsToDestination = (trips, destination) => {
  if (!trips || !destination) {
    return [];
  }

  return trips
    .filter((t) => t.destination === destination)
    .map((t) => ({ zone: t.origin, trips: t.trips }));
};

/**
 * Calculate maximum trips for normalization
 * @param {Array} zoneTrips - Array of { zone, trips } objects
 * @returns {number} Maximum trips value
 */
export const calculateMaxTrips = (zoneTrips) => {
  if (!zoneTrips || zoneTrips.length === 0) {
    return 0;
  }

  return Math.max(...zoneTrips.map((t) => t.trips));
};

/**
 * Calculate total trips for percentage calculation
 * @param {Array} zoneTrips - Array of { zone, trips } objects
 * @returns {number} Total trips value
 */
export const calculateTotalTrips = (zoneTrips) => {
  if (!zoneTrips || zoneTrips.length === 0) {
    return 0;
  }

  return zoneTrips.reduce((sum, t) => sum + t.trips, 0);
};

/**
 * Create a map of zone -> trips for quick lookup
 * @param {Array} zoneTrips - Array of { zone, trips } objects
 * @returns {Map} Map of zone name to trips count
 */
export const createTripsMap = (zoneTrips) => {
  const map = new Map();

  if (zoneTrips) {
    zoneTrips.forEach(({ zone, trips }) => {
      map.set(zone, trips);
    });
  }

  return map;
};

/**
 * Get zone data for visualization based on selection
 * @param {Array} trips - Current filtered trips
 * @param {string} origin - Selected origin (or null)
 * @param {string} destination - Selected destination (or null)
 * @returns {object} { zoneTripsMap, totalTrips, maxTrips }
 */
export const getZoneVisualizationData = (trips, origin, destination) => {
  let zoneTrips = [];

  if (origin && destination) {
    // Both selected: show all origins that send trips TO the destination
    // This shows how many trips from each zone went to the selected destination
    zoneTrips = getTripsToDestination(trips, destination);
  } else if (origin) {
    // Origin selected: show destinations with trip counts
    zoneTrips = getTripsFromOrigin(trips, origin);
  } else if (destination) {
    // Destination selected: show origins with trip counts
    zoneTrips = getTripsToDestination(trips, destination);
  }

  const totalTrips = calculateTotalTrips(zoneTrips);
  const maxTrips = calculateMaxTrips(zoneTrips);
  const zoneTripsMap = createTripsMap(zoneTrips);

  return { zoneTripsMap, totalTrips, maxTrips };
};

/**
 * Format time slot for display
 * @param {string} timeSlot - Time slot string (e.g., "08:00 - 09:00")
 * @returns {string} Formatted time slot
 */
export const formatTimeSlot = (timeSlot) => {
  return timeSlot || '';
};

/**
 * Format day type for display
 * @param {string} dayType - Day type string (e.g., "Feriali")
 * @returns {string} Formatted day type with translation
 */
export const formatDayType = (dayType) => {
  const translations = {
    Feriali: 'Weekdays',
    Festivi: 'Holidays',
    Sabato: 'Saturday',
    Domenica: 'Sunday',
  };

  return translations[dayType] || dayType;
};
